import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useUploadQueue } from '../context/UploadQueueContext';
import { refreshRef } from '../App';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  Folder, Upload, Trash2, Download, Edit2, MessageSquare,
  LogOut, Users, ChevronRight, Home, Search, X, Check, FolderPlus,
  Image, Video, Music, FileText, Archive, Eye, FolderOpen,
  ArrowLeft, HardDrive, Loader, AlertCircle, Move, Grid, List,
  Plus, Play, MoreHorizontal, Calendar, Building2, BarChart2
, ArrowUpDown } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import AttendancePage from './AttendancePage';
import AdminAttendancePage from './AdminAttendancePage';
import ClientsPage from './ClientsPage';
import ProjectPage from './ProjectPage';
import StaffAttendanceView from './StaffAttendanceView';
import AdminDashboard from './AdminDashboard';

/* ─────────────────────────────────────────────
   DESIGN TOKENS — Light theme matching reference
───────────────────────────────────────────── */
const T = {
  bg:         '#f8f7ff',
  surface:    '#ffffff',
  card:       '#ffffff',
  border:     '#e8e4f3',
  borderL:    '#f0edfb',
  purple:     '#7c3aed',
  purpleL:    '#8b5cf6',
  purpleBg:   '#ede9fe',
  purpleText: '#6d28d9',
  text:       '#1a1035',
  textMd:     '#4a3f6b',
  textSm:     '#8b7fb0',
  textXs:     '#b8b3cc',
  green:      '#059669',
  greenBg:    '#d1fae5',
  blue:       '#2563eb',
  blueBg:     '#dbeafe',
  orange:     '#d97706',
  orangeBg:   '#fef3c7',
  red:        '#dc2626',
  redBg:      '#fee2e2',
  font:       "'Poppins', sans-serif",
  shadow:     '0 1px 4px rgba(124,58,237,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  shadowMd:   '0 4px 16px rgba(124,58,237,0.1), 0 2px 6px rgba(0,0,0,0.05)',
};

/* ─────────────────────────────────────────────
   GLOBAL STYLES
───────────────────────────────────────────── */
const G = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: #f0edfb; }
  ::-webkit-scrollbar-thumb { background: #c4b5fd; border-radius: 6px; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }

  .nav-item { transition: all 0.15s ease; cursor: pointer; border-radius: 10px; }
  .nav-item:hover { background: #f5f3ff !important; }
  .nav-item.active { background: #ede9fe !important; }

  .row-item { transition: background 0.15s; }
  .row-item:hover { background: #faf9ff !important; }

  .folder-row { transition: background 0.15s; cursor: pointer; }
  .folder-row:hover { background: #f5f3ff !important; }
  .folder-row:hover .row-actions { opacity: 1 !important; }
  .row-actions { opacity: 1; transition: opacity 0.15s; }

  .file-grid-card { transition: all 0.2s ease; cursor: pointer; }
  .file-grid-card:hover { border-color: #c4b5fd !important; box-shadow: 0 6px 20px rgba(124,58,237,0.1) !important; transform: translateY(-2px); }
  .file-grid-card:hover .grid-overlay { opacity: 1 !important; }

  .action-icon-btn { transition: all 0.15s; }
  .action-icon-btn:hover { background: #ede9fe !important; color: #7c3aed !important; }

  .nova-input {
    border: 1.5px solid #e8e4f3;
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 14px;
    font-family: 'Poppins', sans-serif;
    font-weight: 400;
    color: #1a1035;
    background: #faf9ff;
    width: 100%;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .nova-input:focus {
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124,58,237,0.08);
    background: #fff;
  }
  .nova-input::placeholder { color: #c4b5fd; }
  .nova-select { cursor: pointer; }
  .nova-select:focus { outline: none; border-color: #7c3aed; }
`;

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const fmtBytes = (b) => {
  if (!b || b === 0) return '0 B';
  const k = 1024, s = ['B','KB','MB','GB','TB'];
  const i = Math.floor(Math.log(b)/Math.log(k));
  return parseFloat((b/Math.pow(k,i)).toFixed(2))+' '+s[i];
};

const getFileType = (name) => {
  const e = name?.split('.').pop()?.toLowerCase();
  if (['jpg','jpeg','png','gif','webp','svg','heic','bmp'].includes(e)) return 'image';
  if (['mp4','mov','avi','mkv','wmv','webm','m4v','mts','mxf','flv'].includes(e)) return 'video';
  if (['mp3','wav','aac','flac','m4a','ogg'].includes(e)) return 'audio';
  if (['zip','rar','7z','tar','gz'].includes(e)) return 'archive';
  if (e === 'pdf') return 'pdf';
  return 'file';
};

const FILE_META = {
  image:   { color:'#059669', bg:'#d1fae5', Icon: Image },
  video:   { color:'#7c3aed', bg:'#ede9fe', Icon: Video },
  audio:   { color:'#d97706', bg:'#fef3c7', Icon: Music },
  archive: { color:'#dc2626', bg:'#fee2e2', Icon: Archive },
  pdf:     { color:'#2563eb', bg:'#dbeafe', Icon: FileText },
  file:    { color:'#6b7280', bg:'#f3f4f6', Icon: FileText },
};

/* ─────────────────────────────────────────────
   BUTTON COMPONENTS
───────────────────────────────────────────── */
const BtnPrimary = ({ children, onClick, type='button', disabled, small }) => (
  <button type={type} onClick={onClick} disabled={disabled}
    style={{ display:'flex', alignItems:'center', gap:6, background:'#7c3aed', color:'#fff', border:'none', borderRadius:10, padding: small ? '8px 14px' : '10px 18px', fontSize: small ? 13 : 14, fontWeight:600, cursor:'pointer', fontFamily:T.font, whiteSpace:'nowrap', boxShadow:'0 2px 8px rgba(124,58,237,0.25)', transition:'all 0.2s', opacity: disabled ? 0.6 : 1 }}>
    {children}
  </button>
);

const BtnSecondary = ({ children, onClick, type='button', small }) => (
  <button type={type} onClick={onClick}
    style={{ display:'flex', alignItems:'center', gap:6, background:'#fff', color:T.textMd, border:`1.5px solid ${T.border}`, borderRadius:10, padding: small ? '7px 13px' : '9px 16px', fontSize: small ? 13 : 14, fontWeight:500, cursor:'pointer', fontFamily:T.font, whiteSpace:'nowrap', transition:'all 0.2s' }}>
    {children}
  </button>
);

const BtnGreen = ({ children, onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ display:'flex', alignItems:'center', gap:6, background:'#059669', color:'#fff', border:'none', borderRadius:10, padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:T.font, whiteSpace:'nowrap', boxShadow:'0 2px 8px rgba(5,150,105,0.2)', transition:'all 0.2s', opacity: disabled ? 0.6 : 1 }}>
    {children}
  </button>
);

const IconBtn = ({ children, onClick, title, active }) => (
  <button className="action-icon-btn" onClick={onClick} title={title}
    style={{ background: active ? '#ede9fe' : 'transparent', border:'none', color: active ? '#7c3aed' : T.textSm, cursor:'pointer', width:34, height:34, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}>
    {children}
  </button>
);

/* ─────────────────────────────────────────────
   BADGE
───────────────────────────────────────────── */
const Badge = ({ type, label }) => {
  const cfg = {
    compressing: { bg:'#ede9fe', color:'#6d28d9', border:'#c4b5fd' },
    ready:       { bg:'#d1fae5', color:'#065f46', border:'#6ee7b7' },
    failed:      { bg:'#fee2e2', color:'#991b1b', border:'#fca5a5' },
    pending:     { bg:'#fef3c7', color:'#92400e', border:'#fcd34d' },
  };
  const c = cfg[type] || cfg.pending;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:c.bg, border:`1px solid ${c.border}`, color:c.color, fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, whiteSpace:'nowrap' }}>
      {type==='compressing' && <Loader size={9} style={{animation:'spin 1s linear infinite'}}/>}
      {label}
    </span>
  );
};

/* ─────────────────────────────────────────────
   FILE THUMBNAIL
───────────────────────────────────────────── */
function FileThumbnail({ file, size='list', onClick }) {
  const type = getFileType(file.name);
  const { color, bg, Icon } = FILE_META[type] || FILE_META.file;
  const isCompressed = file.preview_status === 'ready' && file.bunny_video_id;
  const bunnyThumb = isCompressed ? `https://vz-78d09846-953.b-cdn.net/${file.bunny_video_id}/thumbnail.jpg` : null;
  const [imgErr, setImgErr] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);

  useEffect(() => {
    if (type === 'image' && size === 'grid') {
      api.get(`/files/${file.id}/preview-url`).then(r => setImgUrl(r.data.url)).catch(() => {});
    }
  }, [file.id, type, size]);

  if (size === 'grid') {
    const src = type === 'video' ? bunnyThumb : imgUrl;
    if (src && !imgErr) {
      return (
        <div onClick={onClick} style={{ position:'relative', width:'100%', height:140, overflow:'hidden', background:'#f3f4f6', cursor:'pointer', borderRadius:'10px 10px 0 0' }}>
          <img src={src} alt={file.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={() => setImgErr(true)}/>
          {type === 'video' && (
            <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.2)' }}>
              <div style={{ width:38, height:38, borderRadius:'50%', background:'rgba(124,58,237,0.85)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Play size={15} color="#fff" fill="#fff"/>
              </div>
            </div>
          )}
        </div>
      );
    }
    return (
      <div onClick={onClick} style={{ width:'100%', height:140, background:bg, display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'10px 10px 0 0', cursor:'pointer' }}>
        <Icon size={40} color={color}/>
      </div>
    );
  }

  return (
    <div style={{ width:38, height:38, borderRadius:9, background:bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
      <Icon size={17} color={color}/>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PREVIEW MODAL
───────────────────────────────────────────── */
function PreviewModal({ file, onClose }) {
  const [urlData, setUrlData] = useState(null);
  const [loading, setLoading] = useState(true);
  const type = getFileType(file.name);

  useEffect(() => {
    api.get(`/files/${file.id}/preview-url`).then(r => { setUrlData(r.data); setLoading(false); }).catch(() => { toast.error('Preview failed'); onClose(); });
  }, [file.id]);

  useEffect(() => {
    if (!urlData || urlData.is_compressed) return;
    if (!['processing','pending'].includes(file.preview_status)) return;
    const iv = setInterval(async () => {
      const r = await api.get(`/files/${file.id}/compression-status`).catch(() => null);
      if (r?.data?.preview_status === 'ready') { const u = await api.get(`/files/${file.id}/preview-url`); setUrlData(u.data); clearInterval(iv); }
    }, 8000);
    return () => clearInterval(iv);
  }, [urlData, file]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, padding:20, backdropFilter:'blur(4px)', fontFamily:T.font }}>
      <div style={{ background:'#fff', border:`1px solid ${T.border}`, borderRadius:16, width:'100%', maxWidth:960, maxHeight:'92vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:T.shadowMd }}>
        <div style={{ display:'flex', alignItems:'center', padding:'16px 20px', borderBottom:`1px solid ${T.border}`, gap:12 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:15, color:T.text }}>{file.name}</div>
            <div style={{ fontWeight:400, fontSize:12, color:T.textSm, marginTop:2 }}>{fmtBytes(file.size)} · {type.toUpperCase()}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {urlData?.is_compressed && <Badge type="ready" label={`Compressed · ${fmtBytes(urlData.preview_size)}`}/>}
            {['processing','pending'].includes(file.preview_status) && !urlData?.is_compressed && <Badge type="compressing" label="Compressing..."/>}
            <IconBtn onClick={onClose} title="Close"><X size={16}/></IconBtn>
          </div>
        </div>
        <div style={{ flex:1, overflow:'auto', display:'flex', alignItems:'center', justifyContent:'center', background:'#0f0a1a', minHeight:300 }}>
          {loading && <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:12 }}><div style={{ width:28, height:28, border:'3px solid rgba(255,255,255,0.15)', borderTop:'3px solid #fff', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/><span style={{ color:'rgba(255,255,255,0.5)', fontSize:13 }}>Loading...</span></div>}
          {!loading && urlData?.is_bunny && type==='video' && <iframe src={`${urlData.url}?autoplay=true&responsive=true`} style={{ width:'100%', height:'75vh', border:'none' }} allow="autoplay;encrypted-media" allowFullScreen title={file.name}/>}
          {!loading && !urlData?.is_bunny && type==='video' && <video src={urlData?.url} controls autoPlay style={{ width:'100%', maxHeight:'75vh', background:'#000' }} playsInline/>}
          {!loading && type==='image' && <img src={urlData?.url} alt={file.name} style={{ maxWidth:'100%', maxHeight:'78vh', objectFit:'contain' }}/>}
          {!loading && type==='audio' && <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:14, padding:48 }}><Music size={48} color="#d97706"/><audio src={urlData?.url} controls autoPlay/></div>}
          {!loading && type==='pdf' && <iframe src={urlData?.url} style={{ width:'100%', height:'75vh', border:'none' }} title={file.name}/>}
        </div>
        {file.remarks && <div style={{ display:'flex', alignItems:'center', gap:8, padding:'12px 20px', borderTop:`1px solid ${T.border}`, background:'#faf9ff' }}><MessageSquare size={13} color={T.purple}/><span style={{ fontSize:13, color:T.textMd }}>{file.remarks}</span></div>}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   STORAGE WIDGET — circular like reference
───────────────────────────────────────────── */
function StorageWidget() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get('/files/stats/storage').then(r => setStats(r.data)).catch(()=>{}); }, []);
  if (!stats) return null;
  const pct = stats.percent || 0;
  const r = 34, circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div style={{ margin:'12px 14px', background:T.surface, border:`1px solid ${T.border}`, borderRadius:14, padding:'16px', boxShadow:T.shadow }}>
      <div style={{ fontWeight:700, fontSize:11, color:T.textSm, textTransform:'uppercase', letterSpacing:1.2, marginBottom:14 }}>STORAGE</div>
      <div style={{ display:'flex', alignItems:'center', gap:14 }}>
        <div style={{ position:'relative', flexShrink:0 }}>
          <svg width={80} height={80} style={{ transform:'rotate(-90deg)' }}>
            <circle cx={40} cy={40} r={r} fill="none" stroke="#ede9fe" strokeWidth={7}/>
            <circle cx={40} cy={40} r={r} fill="none" stroke="#7c3aed" strokeWidth={7} strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" style={{ transition:'stroke-dasharray 0.6s ease' }}/>
          </svg>
          <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:14, color:T.text }}>{pct}%</div>
        </div>
        <div>
          <div style={{ fontWeight:600, fontSize:13, color:T.text }}>{fmtBytes(stats.used)}</div>
          <div style={{ fontWeight:400, fontSize:12, color:T.textSm, marginTop:2 }}>of {fmtBytes(stats.limit)} used</div>
        </div>
      </div>
      <div style={{ marginTop:12, height:5, background:'#ede9fe', borderRadius:10, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:'linear-gradient(90deg, #7c3aed, #8b5cf6)', borderRadius:10, transition:'width 0.6s' }}/>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MODAL WRAPPER
───────────────────────────────────────────── */
function ModalWrap({ children, onClose, title, subtitle, actions }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, backdropFilter:'blur(4px)', fontFamily:T.font }}>
      <div style={{ background:'#fff', border:`1px solid ${T.border}`, borderRadius:16, padding:'28px', width:'100%', maxWidth:440, boxShadow:T.shadowMd, animation:'fadeUp 0.22s ease' }}>
        <div style={{ fontWeight:700, fontSize:18, color:T.text, marginBottom:4 }}>{title}</div>
        {subtitle && <div style={{ fontWeight:400, fontSize:14, color:T.textSm, marginBottom:18 }}>{subtitle}</div>}
        {children}
        {actions && <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:20 }}>{actions}</div>}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   BULK MOVE CONTENT
───────────────────────────────────────────── */
function BulkMoveContent({ selectedItems, currentFolderId, onDone, onClose }) {
  const [allFolders, setAllFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const load = async () => {
      const result = [];
      const loadLevel = async (parentId, depth) => {
        try {
          const res = await api.get('/folders', { params: { parent_id: parentId || 'null' } });
          for (const f of res.data) {
            const movingIds = selectedItems.filter(s => s.type === 'folder').map(s => s.id);
            if (!movingIds.includes(f.id)) { result.push({ ...f, depth }); await loadLevel(f.id, depth + 1); }
          }
        } catch {}
      };
      await loadLevel(null, 0);
      setAllFolders(result); setLoading(false);
    };
    load();
  }, []);
  return (
    <div>
      <div style={{ fontWeight:500, fontSize:13, color:T.textSm, marginBottom:14, padding:'10px 12px', background:'#faf9ff', borderRadius:9, border:`1px solid ${T.border}` }}>
        Moving {selectedItems.length} item{selectedItems.length>1?'s':''} — {selectedItems.filter(s=>s.type==='file').length} files, {selectedItems.filter(s=>s.type==='folder').length} folders
      </div>
      {loading ? <div style={{ color:T.textSm, fontSize:14, padding:'10px 0' }}>Loading folders...</div> : (
        <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:280, overflow:'auto' }}>
          <button onClick={() => onDone(null)} disabled={!currentFolderId}
            style={{ display:'flex', alignItems:'center', gap:9, padding:'11px 14px', background:'#faf9ff', border:`1.5px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:14, fontWeight:500, cursor:'pointer', fontFamily:T.font, textAlign:'left', opacity:!currentFolderId?0.4:1 }}>
            <Home size={15} color={T.purple}/> Root — NOVA Drive
          </button>
          {allFolders.filter(f => f.id !== currentFolderId).map(folder => (
            <button key={folder.id} onClick={() => onDone(folder.id)}
              style={{ display:'flex', alignItems:'center', gap:9, padding:'11px 14px', paddingLeft: 14 + folder.depth * 16, background:'#faf9ff', border:`1.5px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:14, fontWeight:400, cursor:'pointer', fontFamily:T.font, textAlign:'left' }}>
              <Folder size={14} color={T.purple}/>
              <span style={{ color:T.textXs, marginRight:2 }}>{'›'.repeat(folder.depth)}</span>{folder.name}
            </button>
          ))}
        </div>
      )}
      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}>
        <BtnSecondary onClick={onClose}>Cancel</BtnSecondary>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MOVE MODAL
───────────────────────────────────────────── */
function MoveModal({ item, itemType, currentFolderId, onClose, onMoved }) {
  const [allFolders, setAllFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const load = async () => {
      const result = [];
      const loadLevel = async (parentId, depth) => {
        try {
          const res = await api.get('/folders', { params: { parent_id: parentId || 'null' } });
          for (const f of res.data) { result.push({ ...f, depth }); await loadLevel(f.id, depth + 1); }
        } catch {}
      };
      await loadLevel(null, 0);
      setAllFolders(result); setLoading(false);
    };
    load();
  }, []);
  const moveTo = async (targetId) => {
    try {
      if (itemType === 'file') await api.put(`/files/${item.id}/move`, { folder_id: targetId });
      else await api.put(`/folders/${item.id}/move`, { parent_id: targetId });
      toast.success('Moved successfully'); onMoved(); onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Move failed'); }
  };
  return (
    <ModalWrap title={`Move "${item.name}"`} subtitle="Select destination folder" onClose={onClose}>
      {loading ? <div style={{ color:T.textSm, fontSize:14 }}>Loading...</div> : (
        <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:300, overflow:'auto' }}>
          <button onClick={() => moveTo(null)} disabled={!currentFolderId}
            style={{ display:'flex', alignItems:'center', gap:9, padding:'11px 14px', background:'#faf9ff', border:`1.5px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:14, fontWeight:500, cursor:'pointer', fontFamily:T.font, opacity:!currentFolderId?0.4:1 }}>
            <Home size={15} color={T.purple}/> Root — NOVA Drive
          </button>
          {allFolders.filter(f => f.id !== item.id && f.id !== currentFolderId).map(folder => (
            <button key={folder.id} onClick={() => moveTo(folder.id)}
              style={{ display:'flex', alignItems:'center', gap:9, padding:'11px 14px', paddingLeft: 14 + folder.depth * 16, background:'#faf9ff', border:`1.5px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:14, fontWeight:400, cursor:'pointer', fontFamily:T.font, textAlign:'left' }}>
              <Folder size={14} color={T.purple}/>
              <span style={{ color:T.textXs }}>{'›'.repeat(folder.depth)}</span> {folder.name}
            </button>
          ))}
        </div>
      )}
      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}>
        <BtnSecondary onClick={onClose}>Cancel</BtnSecondary>
      </div>
    </ModalWrap>
  );
}

/* ─────────────────────────────────────────────
   MAIN DASHBOARD
───────────────────────────────────────────── */
export default function Dashboard() {
  const { user, logout } = useAuth();
  const { addToQueue, waitingCount, uploadingItem } = useUploadQueue();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const isSMM = user?.role === 'smm';
  const isEditor = user?.role === 'editor';

  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [activeTab, setActiveTab] = useState('files');
  const [users, setUsers] = useState([]);
  const [remarksModal, setRemarksModal] = useState(null);
  const [remarksText, setRemarksText] = useState('');
  const [renameModal, setRenameModal] = useState(null);
  const [renameText, setRenameText] = useState('');
  const [newUserModal, setNewUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ username:'', password:'', name:'', role:'editor' });
  const [dragOver, setDragOver] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [moveModal, setMoveModal] = useState(null);
  const [moveModalBulk, setMoveModalBulk] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [selectedItems, setSelectedItems] = useState([]);
  const [sortBy, setSortBy] = useState('recent');
const [showSortDrop, setShowSortDrop] = useState(false);
const sortDropRef = useRef(null);
  const fileInputRef = useRef();
  const folderInputRef = useRef();

  const loadContents = useCallback(async (folderId = null) => {
    setLoading(true);
    try {
      const [fRes, dRes] = await Promise.all([
        api.get('/folders', { params: { parent_id: folderId || 'null' } }),
        api.get('/files', { params: { folder_id: folderId || 'null' } }),
      ]);
      setFolders(fRes.data); setFiles(dRes.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadContents(null); }, [loadContents]);
  useEffect(() => {
    refreshRef.current = () => loadContents(currentFolder);
    return () => { refreshRef.current = null; };
  }, [loadContents, currentFolder]);

  // Auto-poll compression
  useEffect(() => {
    const iv = setInterval(async () => {
      const proc = files.filter(f => ['processing','pending'].includes(f.preview_status));
      if (!proc.length) return;
      try {
        const updates = await Promise.all(proc.map(f => api.get(`/files/${f.id}/compression-status`).then(r => r.data)));
        updates.forEach(u => { if (u.preview_status === 'ready') setFiles(prev => prev.map(f => f.id === u.id ? { ...f, ...u } : f)); });
      } catch {}
    }, 15000);
    return () => clearInterval(iv);
  }, [files]);

  const openFolder = async (folder) => {
    setSearchResults(null); setSearchQuery(''); clearSelection();
    setCurrentFolder(folder.id);
    setBreadcrumb(prev => [...prev, { id:folder.id, name:folder.name }]);
    await loadContents(folder.id);
  };

  const navigateTo = async (index) => {
    const fid = index === -1 ? null : breadcrumb[index].id;
    setCurrentFolder(fid);
    setBreadcrumb(index === -1 ? [] : prev => prev.slice(0, index + 1));
    clearSelection();
    await loadContents(fid);
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await api.post('/folders', { name:newFolderName.trim(), parent_id:currentFolder });
      setFolders(prev => [...prev, res.data]);
      setNewFolderName(''); setShowNewFolder(false);
      toast.success('Folder created');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const handleFileUpload = async (fileList, isFolderUpload = false) => {
    const allFiles = Array.from(fileList).filter(f => f.name !== '.DS_Store');
    if (!allFiles.length) return;
    let folderMap = {};
    if (isFolderUpload) {
      const paths = [...new Set(allFiles.map(f => f.webkitRelativePath?.split('/').slice(0,-1).join('/')).filter(Boolean))].sort();
      for (const path of paths) {
        const parts = path.split('/');
        let parentId = currentFolder, built = '';
        for (const part of parts) {
          built = built ? `${built}/${part}` : part;
          if (!folderMap[built]) {
            try { const r = await api.post('/folders', { name:part, parent_id:parentId }); folderMap[built] = r.data.id; parentId = r.data.id; }
            catch { parentId = folderMap[built] || currentFolder; }
          } else { parentId = folderMap[built]; }
        }
      }
    }
        // Wait briefly to ensure all folder creation API calls are settled
    await new Promise(r => setTimeout(r, 500));

    allFiles.forEach(file => {
      let target = currentFolder;
      if (isFolderUpload && file.webkitRelativePath) {
        const pk = file.webkitRelativePath.split('/').slice(0,-1).join('/');
        if (pk && folderMap[pk]) target = folderMap[pk];
        // Also try partial path match if full path not found
        if (!target || target === currentFolder) {
          const parts = pk.split('/');
          while (parts.length > 0) {
            const partial = parts.join('/');
            if (folderMap[partial]) { target = folderMap[partial]; break; }
            parts.pop();
          }
        }
      }
      addToQueue([file], target);
    });
    toast(`${allFiles.length} file${allFiles.length>1?'s':''} added to queue`, { icon:'📤' });
    setTimeout(() => loadContents(currentFolder), 3000);
  };

  const handleDownload = async (file) => {
    try { const r = await api.get(`/files/${file.id}/download`); const a = document.createElement('a'); a.href = r.data.download_url; a.download = r.data.filename; a.click(); }
    catch { toast.error('Download failed'); }
  };

  const handleDelete = async (type, id, name) => {
    if (!window.confirm(`Delete "${name}"? Cannot be undone.`)) return;
    try {
      if (type === 'file') { await api.delete(`/files/${id}`); setFiles(prev => prev.filter(f => f.id !== id)); }
      else { await api.delete(`/folders/${id}`); setFolders(prev => prev.filter(f => f.id !== id)); }
      toast.success('Deleted');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const saveRemarks = async () => {
    try {
      await api.put(`/files/${remarksModal.id}/remarks`, { remarks:remarksText });
      setFiles(prev => prev.map(f => f.id === remarksModal.id ? { ...f, remarks:remarksText } : f));
      setRemarksModal(null); toast.success('Remarks saved');
    } catch { toast.error('Failed'); }
  };

  const saveRename = async () => {
    try {
      if (renameModal.type === 'file') { await api.put(`/files/${renameModal.id}/rename`, { name:renameText }); setFiles(prev => prev.map(f => f.id === renameModal.id ? { ...f, name:renameText } : f)); }
      else { await api.put(`/folders/${renameModal.id}`, { name:renameText }); setFolders(prev => prev.map(f => f.id === renameModal.id ? { ...f, name:renameText } : f)); }
      setRenameModal(null); toast.success('Renamed');
    } catch { toast.error('Rename failed'); }
  };

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults(null); return; }
    try { const r = await api.get('/files/search', { params:{q} }); setSearchResults(r.data); } catch {}
  };

  const loadUsers = async () => { try { const r = await api.get('/auth/users'); setUsers(r.data); } catch { toast.error('Failed'); } };

  const createUser = async () => {
    try {
      await api.post('/auth/users', newUser);
      toast.success('User created'); setNewUserModal(false);
      setNewUser({ username:'', password:'', name:'', role:'editor' }); loadUsers();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Delete user "${name}"?`)) return;
    try { await api.delete(`/auth/users/${id}`); setUsers(prev => prev.filter(u => u.id !== id)); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  const toggleSelect = (id, type, item) => {
    setSelectedItems(prev => {
      const exists = prev.find(s => s.id === id && s.type === type);
      return exists ? prev.filter(s => !(s.id === id && s.type === type)) : [...prev, { id, type, item }];
    });
  };
  const isSelected = (id, type) => selectedItems.some(s => s.id === id && s.type === type);
  const selectAll = () => setSelectedItems([...folders.map(f => ({ id:f.id, type:'folder', item:f })), ...displayFiles.map(f => ({ id:f.id, type:'file', item:f }))]);
  const clearSelection = () => setSelectedItems([]);

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedItems.length} items? Cannot be undone.`)) return;
    for (const sel of selectedItems) {
      try {
        if (sel.type === 'file') { await api.delete(`/files/${sel.id}`); setFiles(prev => prev.filter(f => f.id !== sel.id)); }
        else { await api.delete(`/folders/${sel.id}`); setFolders(prev => prev.filter(f => f.id !== sel.id)); }
      } catch {}
    }
    clearSelection(); toast.success(`${selectedItems.length} items deleted`);
  };

  const handleBulkDownload = async () => {
    const fileItems = selectedItems.filter(s => s.type === 'file');
    if (!fileItems.length) { toast('No files selected', { icon:'ℹ️' }); return; }
    for (const sel of fileItems) {
      try { const r = await api.get(`/files/${sel.id}/download`); const a = document.createElement('a'); a.href = r.data.download_url; a.download = r.data.filename; a.click(); await new Promise(res => setTimeout(res, 400)); } catch {}
    }
    toast.success(`Downloading ${fileItems.length} files`);
  };

  // Sort files — folders always stay on top, only files sort
const SORT_FNS = {
  recent:    (a,b) => new Date(b.created_at||0)-new Date(a.created_at||0),
  name_asc:  (a,b) => (a.name||'').localeCompare(b.name||''),
  name_desc: (a,b) => (b.name||'').localeCompare(a.name||''),
  oldest:    (a,b) => new Date(a.created_at||0)-new Date(b.created_at||0),
  size_desc: (a,b) => (b.size||0)-(a.size||0),
  size_asc:  (a,b) => (a.size||0)-(b.size||0),
};
const displayFiles   = [...(searchResults || files)].sort(SORT_FNS[sortBy] || SORT_FNS.recent);
const displayFolders = [...folders].sort(SORT_FNS[sortBy] || SORT_FNS.recent);
  const ROLE_CFG = { admin:{ bg:'#ede9fe', color:'#6d28d9' }, editor:{ bg:'#d1fae5', color:'#065f46' }, smm:{ bg:'#dbeafe', color:'#1d4ed8' } };


  const NAV_ITEMS = [
    ...(isAdmin ? [{ id:'dashboard', label:'Dashboard', Icon:BarChart2 }] : []),
    { id:'files', label:'File Storage', Icon:Folder },
    ...(isAdmin ? [{ id:'users', label:'Manage Users', Icon:Users }] : []),
    { id:'attendance', label: isAdmin ? 'Attendance Admin' : 'Attendance', Icon:Calendar },
    ...(isSMM ? [{ id:'staffview', label:'Staff Attendance', Icon:Users }] : []),
    ...(isAdmin ? [{ id:'clients', label:'Clients', Icon:Building2 }] : []),
    ...((isAdmin||isSMM||isEditor) ? [{ id:'projects', label:'Projects', Icon:FileText }] : []),
  ];

  return (
    <div style={{ display:'flex', height:'100vh', background:T.bg, fontFamily:T.font, color:T.text, overflow:'hidden' }}>
      <style>{G}</style>

      {/* ══ SIDEBAR ══════════════════════════════════ */}
      <div style={{ width:236, background:T.surface, borderRight:`1px solid ${T.border}`, display:'flex', flexDirection:'column', flexShrink:0, boxShadow:'2px 0 8px rgba(124,58,237,0.04)' }}>
        {/* Logo */}
        <div style={{ padding:'20px 18px 18px', borderBottom:`1px solid ${T.borderL}` }}>
          <div style={{ display:'flex', alignItems:'center', gap:11 }}>
            <div style={{ width:42, height:42, borderRadius:12, background:'#ede9fe', border:`1px solid #c4b5fd`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {/* Replace with <img src="/nova-logo.svg" style={{width:28,height:28,objectFit:'contain'}}/> */}
              <span style={{ fontWeight:800, fontSize:18, color:'#7c3aed' }}>N</span>
            </div>
            <div>
              <div style={{ fontWeight:700, fontSize:15, color:T.text, letterSpacing:0.2 }}>NOVA DRIVE</div>
              <div style={{ fontWeight:400, fontSize:11, color:T.textXs, letterSpacing:0.3 }}>Secure Cloud Storage</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <div style={{ flex:1, padding:'16px 12px', display:'flex', flexDirection:'column', gap:3 }}>
          <div style={{ fontWeight:600, fontSize:11, color:T.textXs, letterSpacing:1.5, textTransform:'uppercase', padding:'0 8px', marginBottom:8 }}>Navigation</div>
          {NAV_ITEMS.map(({ id, label, Icon }) => {
            const active = activeTab === id;
            return (
              <div key={id} className={`nav-item ${active ? 'active' : ''}`}
                onClick={() => { setActiveTab(id); if (id==='users') loadUsers(); }}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 12px', background: active ? '#ede9fe' : 'transparent', borderRadius:10 }}>
                <Icon size={17} color={active ? '#7c3aed' : T.textSm}/>
                <span style={{ fontWeight: active ? 600 : 500, fontSize:14, color: active ? '#6d28d9' : T.textMd }}>{label}</span>
              </div>
            );
          })}
        </div>

        <StorageWidget />

        {/* User */}
        <div style={{ padding:'14px 16px', borderTop:`1px solid ${T.borderL}`, display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:'#ede9fe', border:`1px solid #c4b5fd`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:700, color:'#6d28d9', flexShrink:0 }}>
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div style={{ flex:1, overflow:'hidden' }}>
            <div style={{ fontWeight:600, fontSize:13, color:T.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{user?.name}</div>
            <div style={{ fontWeight:400, fontSize:11, color:T.textXs, textTransform:'capitalize' }}>{user?.role}</div>
          </div>
          <button onClick={() => { logout(); navigate('/login'); }} title="Sign out"
            style={{ background:'#faf9ff', border:`1px solid ${T.border}`, color:T.textSm, cursor:'pointer', width:30, height:30, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s' }}>
            <LogOut size={14}/>
          </button>
        </div>
      </div>

      {/* ══ MAIN ═════════════════════════════════════ */}
{/* ── TOP BAR */}
{/* ══ MAIN ═════════════════════════════════════ */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:T.bg }}>
<div style={{display:'flex',alignItems:'center',padding:'0 28px',height:62,background:T.surface,borderBottom:`1px solid ${T.border}`,gap:12,flexShrink:0,boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>

  {activeTab==='files' ? (
    /* ── FILE STORAGE TOP BAR — full navigation + search + actions ── */
    <>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <button onClick={()=>breadcrumb.length>0&&navigateTo(breadcrumb.length-2)}
          style={{width:32,height:32,borderRadius:8,background:breadcrumb.length>0?'#f5f3ff':'#faf9ff',border:`1px solid ${T.border}`,color:breadcrumb.length>0?T.purple:T.textXs,cursor:breadcrumb.length>0?'pointer':'not-allowed',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s'}}>
          <ArrowLeft size={15}/>
        </button>
        <button onClick={()=>navigateTo(-1)}
          style={{width:32,height:32,borderRadius:8,background:'#faf9ff',border:`1px solid ${T.border}`,color:T.textSm,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s'}}>
          <Home size={15}/>
        </button>
        <div style={{display:'flex',alignItems:'center',background:'#f5f3ff',border:'1px solid #e9d5ff',borderRadius:20,padding:'6px 14px',gap:6,cursor:'pointer'}} onClick={()=>navigateTo(-1)}>
          <span style={{fontWeight:500,fontSize:14,color:T.textMd}}>My Drive</span>
          {breadcrumb.map((c,i)=>(
            <React.Fragment key={c.id}>
              <ChevronRight size={13} color={T.textXs}/>
              <span style={{fontWeight:i===breadcrumb.length-1?600:400,fontSize:14,color:i===breadcrumb.length-1?T.purple:T.textMd,cursor:'pointer',maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}
                onClick={e=>{e.stopPropagation();navigateTo(i);}}>{c.name}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
      <div style={{flex:1}}/>
      <div style={{display:'flex',alignItems:'center',background:'#faf9ff',border:`1px solid ${T.border}`,borderRadius:10,padding:'8px 14px',gap:8,minWidth:220}}>
        <Search size={15} color={T.textXs}/>
        <input style={{background:'transparent',border:'none',outline:'none',color:T.text,fontSize:14,flex:1,fontFamily:T.font,fontWeight:400}} placeholder="Search files and folders..." value={searchQuery} onChange={e=>handleSearch(e.target.value)}/>
        {searchQuery&&<X size={14} color={T.textXs} style={{cursor:'pointer'}} onClick={()=>handleSearch('')}/>}
      </div>
      <div style={{display:'flex',background:'#faf9ff',border:`1px solid ${T.border}`,borderRadius:9,overflow:'hidden'}}>
        {[{m:'list',I:List},{m:'grid',I:Grid}].map(({m,I})=>(
          <button key={m} onClick={()=>setViewMode(m)}
            style={{background:viewMode===m?'#ede9fe':'transparent',border:'none',color:viewMode===m?T.purple:T.textSm,cursor:'pointer',padding:'7px 10px',display:'flex',alignItems:'center',transition:'all 0.15s'}}>
            <I size={16}/>
          </button>
        ))}
      </div>
      <div ref={sortDropRef} style={{position:'relative'}}>
        <button onClick={()=>setShowSortDrop(o=>!o)}
          style={{display:'flex',alignItems:'center',gap:6,background:'#faf9ff',border:`1px solid ${T.border}`,borderRadius:9,padding:'7px 11px',fontSize:12,fontFamily:T.font,color:T.textMd,cursor:'pointer',fontWeight:500}}>
          <ArrowUpDown size={13} color={T.purple}/>
          {{recent:'Recent',name_asc:'Name A→Z',name_desc:'Name Z→A',oldest:'Oldest',size_desc:'Size ↓',size_asc:'Size ↑'}[sortBy]||'Sort'}
        </button>
        {showSortDrop&&(
          <div style={{position:'absolute',top:'calc(100% + 4px)',right:0,zIndex:100,background:'#fff',border:`1px solid ${T.border}`,borderRadius:10,boxShadow:'0 4px 20px rgba(0,0,0,0.1)',minWidth:160,overflow:'hidden'}}>
            {[
              {v:'recent',   l:'Recent Upload'},
              {v:'name_asc', l:'Name A→Z'},
              {v:'name_desc',l:'Name Z→A'},
              {v:'oldest',   l:'Oldest First'},
              {v:'size_desc',l:'Size (Large→Small)'},
              {v:'size_asc', l:'Size (Small→Large)'},
            ].map(s=>(
              <button key={s.v} onClick={()=>{setSortBy(s.v);setShowSortDrop(false);}}
                style={{display:'flex',alignItems:'center',gap:8,width:'100%',padding:'9px 14px',background:sortBy===s.v?'#ede9fe':'transparent',border:'none',cursor:'pointer',fontFamily:T.font,fontSize:12,fontWeight:sortBy===s.v?600:400,color:sortBy===s.v?T.purple:T.text,textAlign:'left'}}>
                {s.l}{sortBy===s.v&&<span style={{marginLeft:'auto',color:T.purple}}>✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <BtnSecondary onClick={()=>setShowNewFolder(true)} small><FolderPlus size={15}/>New Folder</BtnSecondary>
      <BtnPrimary onClick={()=>fileInputRef.current.click()} small><Upload size={15}/>Upload Files</BtnPrimary>
      <BtnGreen onClick={()=>folderInputRef.current.click()}><FolderOpen size={14}/>Upload Folder</BtnGreen>
      <input ref={fileInputRef} type="file" multiple style={{display:'none'}} onChange={e=>handleFileUpload(e.target.files,false)}/>
      <input ref={folderInputRef} type="file" multiple webkitdirectory="" style={{display:'none'}} onChange={e=>handleFileUpload(e.target.files,true)}/>
    </>
  ) : (
    /* ── OTHER TABS — just module name centered ── */
    <>
      <div style={{flex:1}}/>
      <div style={{fontWeight:600,fontSize:14,color:T.textMd,letterSpacing:0.3}}>
        {{
          dashboard:  'Dashboard',
          users:      'Manage Users',
          attendance: 'Attendance',
          staffview:  'Staff Attendance',
          clients:    'Clients',
          projects:   'Project Management',
        }[activeTab]||''}
      </div>
      <div style={{flex:1}}/>
      {activeTab==='users'&&isAdmin&&(
        <BtnPrimary onClick={()=>setNewUserModal(true)} small><Plus size={15}/>Add User</BtnPrimary>
      )}
    </>
  )}
</div>

        {/* ── CONTENT ── */}
<div style={{ flex:1, overflow:'auto', padding: activeTab==='projects' ? '0' : '28px 28px 100px' }}>

          {activeTab === 'files' && (
            <>
              <div style={{ marginBottom:24 }}>
                <h1 style={{ fontWeight:800, fontSize:26, color:T.text, letterSpacing:-0.5, marginBottom:4 }}>File Storage</h1>
                <p style={{ fontWeight:400, fontSize:15, color:T.textSm }}>Manage and organise your files in one secure place</p>
              </div>
              {selectedItems.length > 0 && (
                <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 18px', background:'#fff', border:`1.5px solid #c4b5fd`, borderRadius:12, marginBottom:20, boxShadow:T.shadow }}>
                  <button onClick={clearSelection} style={{ background:'#f5f3ff', border:`1px solid #e9d5ff`, color:T.purple, cursor:'pointer', borderRadius:7, width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center' }}><X size={13}/></button>
                  <span style={{ fontWeight:600, fontSize:14, color:T.text }}>{selectedItems.length} selected</span>
                  <span style={{ fontWeight:400, fontSize:13, color:T.textSm }}>({selectedItems.filter(s=>s.type==='file').length} files, {selectedItems.filter(s=>s.type==='folder').length} folders)</span>
                  <div style={{ flex:1 }}/>
                  <BtnSecondary onClick={selectAll} small>Select All</BtnSecondary>
                  <button onClick={handleBulkDownload} style={{ display:'flex', alignItems:'center', gap:5, background:'#d1fae5', border:'1px solid #6ee7b7', color:'#065f46', cursor:'pointer', borderRadius:8, padding:'6px 13px', fontSize:13, fontWeight:600, fontFamily:T.font }}><Download size={13}/> Download</button>
                  <button onClick={() => setMoveModalBulk(true)} style={{ display:'flex', alignItems:'center', gap:5, background:'#ede9fe', border:'1px solid #c4b5fd', color:T.purpleText, cursor:'pointer', borderRadius:8, padding:'6px 13px', fontSize:13, fontWeight:600, fontFamily:T.font }}><Move size={13}/> Move</button>
                  {isAdmin && <button onClick={handleBulkDelete} style={{ display:'flex', alignItems:'center', gap:5, background:'#fee2e2', border:'1px solid #fca5a5', color:'#dc2626', cursor:'pointer', borderRadius:8, padding:'6px 13px', fontSize:13, fontWeight:600, fontFamily:T.font }}><Trash2 size={13}/> Delete</button>}
                </div>
              )}
              {showNewFolder && (
                <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px', background:'#fff', border:`1.5px solid #c4b5fd`, borderRadius:12, marginBottom:20 }}>
                  <FolderPlus size={16} color={T.purple}/>
                  <input className="nova-input" autoFocus placeholder="New folder name..." value={newFolderName} onChange={e => setNewFolderName(e.target.value)} onKeyDown={e => e.key==='Enter' && createFolder()} style={{ flex:1, background:'#faf9ff', border:`1.5px solid #c4b5fd`, borderRadius:9, padding:'9px 13px', fontSize:14 }}/>
                  <button onClick={createFolder} style={{ background:'#d1fae5', border:'1px solid #6ee7b7', color:'#059669', cursor:'pointer', borderRadius:8, width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center' }}><Check size={14}/></button>
                  <button onClick={() => { setShowNewFolder(false); setNewFolderName(''); }} style={{ background:'#fee2e2', border:'1px solid #fca5a5', color:'#dc2626', cursor:'pointer', borderRadius:8, width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center' }}><X size={14}/></button>
                </div>
              )}
              {loading ? (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:300, gap:14 }}>
                  <div style={{ width:32, height:32, border:`3px solid #ede9fe`, borderTop:`3px solid ${T.purple}`, borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
                  <span style={{ fontWeight:500, fontSize:14, color:T.textSm }}>Loading...</span>
                </div>
              ) : (
                <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files, false); }}
                  style={{ outline: dragOver ? `2px dashed #c4b5fd` : 'none', borderRadius:12, transition:'outline 0.2s', minHeight:200 }}>
                  {!searchResults && displayFolders.length > 0 && (
                    <div style={{ marginBottom:32 }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
                        <h2 style={{ fontWeight:700, fontSize:17, color:T.text }}>Folders</h2>
                        <span style={{ fontWeight:400, fontSize:13, color:T.textSm }}>{displayFolders.length} folder{displayFolders.length!==1?'s':''}</span>
                      </div>
                      {viewMode === 'list' ? (
                        <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:12, overflow:'hidden', boxShadow:T.shadow }}>
                          <div style={{ display:'grid', gridTemplateColumns:'40px 1fr 120px 80px', padding:'11px 16px', borderBottom:`1px solid ${T.borderL}`, background:'#faf9ff' }}>
                            {['','Name','Modified',''].map((h,i) => <span key={i} style={{ fontWeight:600, fontSize:12, color:T.textSm, textTransform:'uppercase', letterSpacing:0.8 }}>{h}</span>)}
                          </div>
                          {displayFolders.map((folder, idx) => (
                            <div key={folder.id} className="folder-row" onDoubleClick={() => openFolder(folder)}
                              style={{ display:'grid', gridTemplateColumns:'40px 1fr 120px auto', padding:'12px 16px', borderBottom: idx<displayFolders.length-1 ? `1px solid ${T.borderL}` : 'none', alignItems:'center', background: isSelected(folder.id,'folder') ? '#faf5ff' : 'transparent' }}>
                              <div onClick={e => { e.stopPropagation(); toggleSelect(folder.id,'folder',folder); }} style={{ cursor:'pointer' }}>
                                <div style={{ width:18, height:18, borderRadius:5, border: isSelected(folder.id,'folder') ? 'none' : `2px solid #c4b5fd`, background: isSelected(folder.id,'folder') ? T.purple : 'transparent', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                  {isSelected(folder.id,'folder') && <Check size={11} color="#fff"/>}
                                </div>
                              </div>
                              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                <div style={{ width:34, height:34, borderRadius:8, background:'#dbeafe', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><Folder size={17} color='#2563eb' fill='#93c5fd'/></div>
                                <div>
                                  <div style={{ fontWeight:600, fontSize:14, color:T.text }}>{folder.name}</div>
                                  <div style={{ fontWeight:400, fontSize:12, color:T.textSm }}>by {folder.created_by_user?.name}</div>
                                </div>
                              </div>
                              <span style={{ fontWeight:400, fontSize:13, color:T.textSm }}>{formatDistanceToNow(new Date(folder.created_at || Date.now()), { addSuffix:true })}</span>
                              <div className="row-actions" style={{ display:'flex', gap:4 }}>
                                <IconBtn onClick={e => { e?.stopPropagation?.(); openFolder(folder); }} title="Open"><Eye size={15}/></IconBtn>
                                <IconBtn onClick={e => { e?.stopPropagation?.(); setMoveModal({ item:folder, itemType:'folder' }); }} title="Move"><Move size={15}/></IconBtn>
                                <IconBtn onClick={e => { e?.stopPropagation?.(); setRenameModal({ id:folder.id, type:'folder' }); setRenameText(folder.name); }} title="Rename"><Edit2 size={15}/></IconBtn>
                                {isAdmin && <IconBtn onClick={e => { e?.stopPropagation?.(); handleDelete('folder',folder.id,folder.name); }} title="Delete"><Trash2 size={15}/></IconBtn>}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(175px,1fr))', gap:12 }}>
                          {displayFolders.map(folder => (
                            <div key={folder.id} className="file-grid-card" onDoubleClick={() => openFolder(folder)}
                              style={{ background:T.surface, border:`1.5px solid ${isSelected(folder.id,'folder') ? '#c4b5fd' : T.border}`, borderRadius:12, padding:'16px', boxShadow:T.shadow, position:'relative' }}>
                              <div onClick={e => { e.stopPropagation(); toggleSelect(folder.id,'folder',folder); }} style={{ position:'absolute', top:10, left:10, cursor:'pointer', zIndex:2 }}>
                                <div style={{ width:18, height:18, borderRadius:5, border: isSelected(folder.id,'folder') ? 'none' : `2px solid #c4b5fd`, background: isSelected(folder.id,'folder') ? T.purple : 'rgba(255,255,255,0.9)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                  {isSelected(folder.id,'folder') && <Check size={10} color="#fff"/>}
                                </div>
                              </div>
                              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8, textAlign:'center', paddingTop:6 }}>
                                <div style={{ width:52, height:52, borderRadius:12, background:'#dbeafe', display:'flex', alignItems:'center', justifyContent:'center' }}><Folder size={24} color='#2563eb' fill='#93c5fd'/></div>
                                <div style={{ fontWeight:600, fontSize:13, color:T.text, wordBreak:'break-word', lineHeight:1.4 }}>{folder.name}</div>
                                <div style={{ fontWeight:400, fontSize:11, color:T.textSm }}>by {folder.created_by_user?.name}</div>
                              </div>
                              <div style={{ display:'flex', justifyContent:'center', gap:4, marginTop:12, paddingTop:10, borderTop:`1px solid ${T.borderL}` }}>
                                <IconBtn onClick={e => { e?.stopPropagation?.(); openFolder(folder); }} title="Open"><Eye size={14}/></IconBtn>
                                <IconBtn onClick={e => { e?.stopPropagation?.(); setMoveModal({ item:folder, itemType:'folder' }); }} title="Move"><Move size={14}/></IconBtn>
                                <IconBtn onClick={e => { e?.stopPropagation?.(); setRenameModal({ id:folder.id, type:'folder' }); setRenameText(folder.name); }} title="Rename"><Edit2 size={14}/></IconBtn>
                                {isAdmin && <IconBtn onClick={e => { e?.stopPropagation?.(); handleDelete('folder',folder.id,folder.name); }} title="Delete"><Trash2 size={14}/></IconBtn>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {displayFiles.length > 0 && (
                    <div>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
                        <h2 style={{ fontWeight:700, fontSize:17, color:T.text }}>{searchResults ? `Results for "${searchQuery}"` : 'Files'}</h2>
                        <span style={{ fontWeight:400, fontSize:13, color:T.textSm }}>{displayFiles.length} file{displayFiles.length!==1?'s':''}</span>
                      </div>
                      {viewMode === 'list' ? (
                        <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:12, overflow:'hidden', boxShadow:T.shadow }}>
                          <div style={{ display:'grid', gridTemplateColumns:'40px 48px 1fr 100px 120px auto', padding:'11px 16px', borderBottom:`1px solid ${T.borderL}`, background:'#faf9ff' }}>
                            {['','','Name','Size','Modified',''].map((h,i) => <span key={i} style={{ fontWeight:600, fontSize:12, color:T.textSm, textTransform:'uppercase', letterSpacing:0.8 }}>{h}</span>)}
                          </div>
                          {displayFiles.map((file, idx) => {
                            const type = getFileType(file.name);
                            const canPreview = ['image','video','audio','pdf'].includes(type);
                            const isCompressing = ['processing','pending'].includes(file.preview_status);
                            const isReady = file.preview_status === 'ready';
                            return (
                              <div key={file.id} className="row-item"
                                style={{ display:'grid', gridTemplateColumns:'40px 48px 1fr 100px 120px auto', padding:'11px 16px', borderBottom: idx<displayFiles.length-1 ? `1px solid ${T.borderL}` : 'none', alignItems:'center', background: isSelected(file.id,'file') ? '#faf5ff' : 'transparent' }}>
                                <div onClick={() => toggleSelect(file.id,'file',file)} style={{ cursor:'pointer' }}>
                                  <div style={{ width:18, height:18, borderRadius:5, border: isSelected(file.id,'file') ? 'none' : `2px solid #c4b5fd`, background: isSelected(file.id,'file') ? T.purple : 'transparent', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                    {isSelected(file.id,'file') && <Check size={11} color="#fff"/>}
                                  </div>
                                </div>
                                <FileThumbnail file={file} size="list"/>
                                <div style={{ overflow:'hidden', paddingRight:12 }}>
                                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                                    <span style={{ fontWeight:600, fontSize:14, color: canPreview ? T.purple : T.text, cursor: canPreview ? 'pointer' : 'default', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }} onClick={() => canPreview && setPreviewFile(file)}>{file.name}</span>
                                    {isCompressing && <Badge type="compressing" label="Compressing"/>}
                                    {isReady && <Badge type="ready" label="HD Ready"/>}
                                  </div>
                                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                    <span style={{ fontWeight:400, fontSize:12, color:T.textSm }}>{file.uploaded_by_user?.name}</span>
                                    {file.remarks && <span style={{ background:'#ede9fe', color:'#6d28d9', fontSize:11, padding:'1px 8px', borderRadius:10, fontWeight:500 }}>📝 {file.remarks}</span>}
                                  </div>
                                </div>
                                <span style={{ fontWeight:500, fontSize:13, color:T.textMd }}>{fmtBytes(file.size)}</span>
                                <span style={{ fontWeight:400, fontSize:13, color:T.textSm }}>{formatDistanceToNow(new Date(file.created_at), { addSuffix:true })}</span>
                                <div style={{ display:'flex', gap:2 }}>
                                  {canPreview && <IconBtn onClick={() => setPreviewFile(file)} title="Preview"><Eye size={15}/></IconBtn>}
                                  <IconBtn onClick={() => handleDownload(file)} title="Download"><Download size={15}/></IconBtn>
                                  <IconBtn onClick={() => setMoveModal({ item:file, itemType:'file' })} title="Move"><Move size={15}/></IconBtn>
                                  <IconBtn onClick={() => { setRemarksModal(file); setRemarksText(file.remarks||''); }} title="Notes"><MessageSquare size={15}/></IconBtn>
                                  <IconBtn onClick={() => { setRenameModal({ id:file.id, type:'file' }); setRenameText(file.name); }} title="Rename"><Edit2 size={15}/></IconBtn>
                                  {isAdmin && <IconBtn onClick={() => handleDelete('file',file.id,file.name)} title="Delete"><Trash2 size={15}/></IconBtn>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12 }}>
                          {displayFiles.map(file => {
                            const type = getFileType(file.name);
                            const canPreview = ['image','video','audio','pdf'].includes(type);
                            const isCompressing = ['processing','pending'].includes(file.preview_status);
                            const isReady = file.preview_status === 'ready';
                            return (
                              <div key={file.id} className="file-grid-card"
                                style={{ background:T.surface, border:`1.5px solid ${isSelected(file.id,'file') ? '#c4b5fd' : T.border}`, borderRadius:12, overflow:'hidden', boxShadow:T.shadow, position:'relative' }}>
                                <div onClick={e => { e.stopPropagation(); toggleSelect(file.id,'file',file); }} style={{ position:'absolute', top:8, left:8, zIndex:3, cursor:'pointer' }}>
                                  <div style={{ width:20, height:20, borderRadius:5, border: isSelected(file.id,'file') ? 'none' : '2px solid rgba(124,58,237,0.4)', background: isSelected(file.id,'file') ? T.purple : 'rgba(255,255,255,0.85)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                    {isSelected(file.id,'file') && <Check size={11} color="#fff"/>}
                                  </div>
                                </div>
                                <div className="grid-overlay" style={{ position:'absolute', top:0, left:0, right:0, height:140, background:'rgba(0,0,0,0.35)', display:'flex', alignItems:'center', justifyContent:'center', gap:8, opacity:0, transition:'opacity 0.2s', zIndex:2, borderRadius:'10px 10px 0 0' }}>
                                  {canPreview && <button onClick={() => setPreviewFile(file)} style={{ background:'rgba(124,58,237,0.9)', border:'none', color:'#fff', cursor:'pointer', borderRadius:8, width:34, height:34, display:'flex', alignItems:'center', justifyContent:'center' }}><Eye size={15}/></button>}
                                  <button onClick={() => handleDownload(file)} style={{ background:'rgba(5,150,105,0.9)', border:'none', color:'#fff', cursor:'pointer', borderRadius:8, width:34, height:34, display:'flex', alignItems:'center', justifyContent:'center' }}><Download size={15}/></button>
                                  {isAdmin && <button onClick={() => handleDelete('file',file.id,file.name)} style={{ background:'rgba(220,38,38,0.9)', border:'none', color:'#fff', cursor:'pointer', borderRadius:8, width:34, height:34, display:'flex', alignItems:'center', justifyContent:'center' }}><Trash2 size={15}/></button>}
                                </div>
                                <FileThumbnail file={file} size="grid" onClick={() => canPreview && setPreviewFile(file)}/>
                                <div style={{ padding:'11px 13px 13px' }}>
                                  <div style={{ fontWeight:600, fontSize:13, color:T.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', marginBottom:5 }} title={file.name}>{file.name}</div>
                                  <div style={{ display:'flex', alignItems:'center', gap:5, flexWrap:'wrap', marginBottom:8 }}>
                                    <span style={{ fontWeight:400, fontSize:12, color:T.textSm }}>{fmtBytes(file.size)}</span>
                                    {isCompressing && <Badge type="compressing" label="Compressing"/>}
                                    {isReady && <Badge type="ready" label="HD Ready"/>}
                                  </div>
                                  <div style={{ display:'flex', gap:3 }}>
                                    <IconBtn onClick={() => setMoveModal({ item:file, itemType:'file' })} title="Move"><Move size={13}/></IconBtn>
                                    <IconBtn onClick={() => { setRemarksModal(file); setRemarksText(file.remarks||''); }} title="Note"><MessageSquare size={13}/></IconBtn>
                                    <IconBtn onClick={() => { setRenameModal({ id:file.id, type:'file' }); setRenameText(file.name); }} title="Rename"><Edit2 size={13}/></IconBtn>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {displayFolders.length===0 && displayFiles.length===0 && !loading && (
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:320, gap:16 }}>
                      <div style={{ width:80, height:80, borderRadius:20, background:'#ede9fe', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {dragOver ? <Upload size={32} color={T.purple}/> : <Folder size={32} color={T.purple}/>}
                      </div>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontWeight:700, fontSize:17, color:T.text, marginBottom:6 }}>{dragOver ? 'Drop to upload' : 'Empty folder'}</div>
                        <div style={{ fontWeight:400, fontSize:14, color:T.textSm }}>Upload files or drag & drop here</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {activeTab === 'users' && isAdmin && (
            <>
              <div style={{ marginBottom:24 }}>
                <h1 style={{ fontWeight:800, fontSize:26, color:T.text, letterSpacing:-0.5, marginBottom:4 }}>Manage Users</h1>
                <p style={{ fontWeight:400, fontSize:15, color:T.textSm }}>{users.length} team members</p>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(290px,1fr))', gap:14 }}>
                {users.map(u => {
                  const rc = ROLE_CFG[u.role] || ROLE_CFG.smm;
                  return (
                    <div key={u.id} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:14, padding:'18px 20px', display:'flex', alignItems:'center', gap:14, boxShadow:T.shadow }}>
                      <div style={{ width:46, height:46, borderRadius:12, background:rc.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:700, color:rc.color, flexShrink:0 }}>
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div style={{ flex:1, overflow:'hidden' }}>
                        <div style={{ fontWeight:700, fontSize:15, color:T.text, marginBottom:3 }}>{u.name}</div>
                        <div style={{ fontWeight:400, fontSize:13, color:T.textSm, marginBottom:6 }}>@{u.username}</div>
                        <span style={{ background:rc.bg, color:rc.color, fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, textTransform:'capitalize' }}>{u.role}</span>
                      </div>
                      <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6}}>
                        {u.is_relieved && <span style={{background:'#fee2e2',color:'#dc2626',fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:12}}>Relieved {u.relieving_date||''}</span>}
                        {u.id !== user.id && !u.is_relieved && <IconBtn onClick={() => deleteUser(u.id, u.name)} title="Delete user"><Trash2 size={15}/></IconBtn>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {activeTab === 'attendance' && (isAdmin ? <AdminAttendancePage /> : <AttendancePage />)}
          {activeTab === 'staffview' && isSMM && <StaffAttendanceView />}
          {activeTab === 'dashboard' && isAdmin && <AdminDashboard />}
          {activeTab === 'clients' && isAdmin && <ClientsPage />}
          {activeTab === 'projects' && <ProjectPage />}

        </div>
      </div>

      {/* ══ MODALS ════════════════════════════════════ */}
      {previewFile && <PreviewModal file={previewFile} onClose={() => setPreviewFile(null)}/>}
      {moveModal && <MoveModal item={moveModal.item} itemType={moveModal.itemType} currentFolderId={currentFolder} onClose={() => setMoveModal(null)} onMoved={() => loadContents(currentFolder)}/>}

      {moveModalBulk && (
        <ModalWrap title={`Move ${selectedItems.length} items`} subtitle="Select destination folder" onClose={() => setMoveModalBulk(false)}>
          <BulkMoveContent selectedItems={selectedItems} currentFolderId={currentFolder}
            onDone={async (targetId) => {
              for (const sel of selectedItems) {
                try {
                  if (sel.type === 'file') await api.put(`/files/${sel.id}/move`, { folder_id: targetId });
                  else await api.put(`/folders/${sel.id}/move`, { parent_id: targetId });
                } catch {}
              }
              toast.success(`Moved ${selectedItems.length} items`);
              clearSelection(); setMoveModalBulk(false); loadContents(currentFolder);
            }} onClose={() => setMoveModalBulk(false)}/>
        </ModalWrap>
      )}

      {remarksModal && (
        <ModalWrap title="Notes & Remarks" subtitle={remarksModal.name} onClose={() => setRemarksModal(null)}
          actions={[<BtnSecondary key="c" onClick={() => setRemarksModal(null)}>Cancel</BtnSecondary>, <BtnPrimary key="s" onClick={saveRemarks}>Save</BtnPrimary>]}>
          <textarea className="nova-input nova-select" value={remarksText} onChange={e => setRemarksText(e.target.value)} placeholder="Add working notes or remarks..." rows={5} style={{ resize:'vertical', lineHeight:1.7, fontSize:14 }}/>
        </ModalWrap>
      )}

      {renameModal && (
        <ModalWrap title="Rename" onClose={() => setRenameModal(null)}
          actions={[<BtnSecondary key="c" onClick={() => setRenameModal(null)}>Cancel</BtnSecondary>, <BtnPrimary key="s" onClick={saveRename}>Rename</BtnPrimary>]}>
          <input className="nova-input" value={renameText} onChange={e => setRenameText(e.target.value)} onKeyDown={e => e.key==='Enter' && saveRename()} autoFocus style={{ fontSize:15 }}/>
        </ModalWrap>
      )}

      {newUserModal && (
        <ModalWrap title="Create New User" subtitle="Set up login credentials" onClose={() => setNewUserModal(false)}
          actions={[<BtnSecondary key="c" onClick={() => setNewUserModal(false)}>Cancel</BtnSecondary>, <BtnPrimary key="s" onClick={createUser}>Create User</BtnPrimary>]}>
          {['name','username','password'].map(f => (
            <div key={f} style={{ marginBottom:16 }}>
              <label style={{ display:'block', fontWeight:600, fontSize:13, color:T.textMd, marginBottom:7 }}>{f.charAt(0).toUpperCase()+f.slice(1)}</label>
              <input className="nova-input" type={f==='password'?'password':'text'} value={newUser[f]} onChange={e => setNewUser(p => ({ ...p, [f]:e.target.value }))} style={{ fontSize:14 }}/>
            </div>
          ))}
          <div style={{ marginBottom:8 }}>
            <label style={{ display:'block', fontWeight:600, fontSize:13, color:T.textMd, marginBottom:7 }}>Role</label>
            <select className="nova-input nova-select" value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role:e.target.value }))} style={{ fontSize:14 }}>
              <option value="editor">Editor</option>
              <option value="smm">SMM</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </ModalWrap>
      )}
    </div>
  );
}